import * as i9 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Component, ChangeDetectionStrategy, Self, Input, HostBinding, ViewChild, forwardRef, Directive, HostListener, Pipe, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormControl, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i11 from '@angular/cdk/portal';
import { DomPortal, PortalModule } from '@angular/cdk/portal';
import * as i6$1 from '@angular/material/form-field';
import { MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import * as i7$1 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import * as i6 from '@angular/material/select';
import { MatSelectModule } from '@angular/material/select';
import * as i4 from '@firestitch/country';
import { FsCountryModule } from '@firestitch/country';
import * as i8 from '@firestitch/skeleton';
import { FsSkeletonModule } from '@firestitch/skeleton';
import * as i2 from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BehaviorSubject, Subject, ReplaySubject, merge, timer, combineLatest, fromEvent } from 'rxjs';
import { switchMap, filter, mapTo, take, tap, takeUntil, map, shareReplay, distinctUntilChanged } from 'rxjs/operators';
import { AsYouType, parsePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { fromFetch } from 'rxjs/fetch';
import { delayedRetry } from '@firestitch/common';
import { formatIncompletePhoneNumber, getExtPrefix } from 'libphonenumber-js/core';
import * as i7 from '@angular/material/core';
import IMask from 'imask';
import { toString } from 'lodash-es';

const FS_PHONE_CONFIG = new InjectionToken('Config for phone package');

const PHONE_CONFIG = new InjectionToken('app-phone-config');

const PHONE_CONFIG_ROOT = new InjectionToken('app-phone-config-root');

const DEFAULT_LOAD_PATH = '/assets/metadata.full.json';
class PhoneMetadataService {
    constructor(_phoneConfig) {
        this._phoneConfig = _phoneConfig;
        this._metadata = new BehaviorSubject(null);
        this._ready$ = new BehaviorSubject(false);
        this._loadMetadata();
    }
    get ready() {
        return this._ready$.getValue();
    }
    get ready$() {
        return this._ready$.asObservable();
    }
    get metadata$() {
        return this._metadata.asObservable();
    }
    get metadata() {
        return this._metadata.getValue();
    }
    _loadMetadata() {
        var _a;
        fromFetch(((_a = this._phoneConfig) === null || _a === void 0 ? void 0 : _a.metadataPath) || DEFAULT_LOAD_PATH)
            .pipe(switchMap((response) => response.json()), delayedRetry(2000, 3))
            .subscribe({
            next: (data) => {
                this._metadata.next(data);
                this._ready$.next(true);
            },
            error: (e) => {
                throw new Error('Phones metadata can not be loaded. ' + e);
            }
        });
    }
}
PhoneMetadataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PhoneMetadataService, deps: [{ token: PHONE_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
PhoneMetadataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PhoneMetadataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PhoneMetadataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [PHONE_CONFIG]
                }] }]; } });

class PhoneService {
    constructor(_metadataService) {
        this._metadataService = _metadataService;
    }
    get metadata() {
        return this._metadataService.metadata;
    }
    formatIncompletePhoneNumber(countryCode, value, country) {
        var _a;
        const typed = new AsYouType(country);
        typed.input(countryCode + value);
        let result = (_a = typed.getNumber()) === null || _a === void 0 ? void 0 : _a.formatInternational().replace(countryCode, '');
        if (!result) {
            return '';
        }
        // Double format to fix TA-T1894
        result = formatIncompletePhoneNumber(result, country, this.metadata);
        return result;
    }
    parsePhoneNumber(value, countryCode) {
        let phoneNumber;
        try {
            phoneNumber = !!countryCode
                ? parsePhoneNumber(value, countryCode)
                : parsePhoneNumber(value);
        }
        catch (e) {
            throw new Error('Can not parse passed phone number. ' + e);
        }
        if (!phoneNumber.country) {
            phoneNumber.country = countryCode;
        }
        return phoneNumber;
    }
    phoneNumberToPhoneValueObject(n) {
        return {
            code: '+' + n.countryCallingCode,
            countryCode: n.country,
            number: this.formatIncompletePhoneNumber('+' + n.countryCallingCode, n.nationalNumber, n.country),
            ext: n.ext,
        };
    }
    isPhoneNumberValid(value) {
        let phoneNumber;
        try {
            if (typeof (value) === 'string') {
                phoneNumber = parsePhoneNumber(value);
            }
            else {
                let phone = value.countryCode + value.number;
                if (value.countryCode.indexOf('+') !== 0) {
                    phone = `+${phone}`;
                }
                phoneNumber = parsePhoneNumber(phone);
            }
            return phoneNumber.isValid() || phoneNumber.isPossible();
        }
        catch (e) {
            return false;
        }
    }
    formatPhoneNumber(value) {
        const phoneNumber = parsePhoneNumber(`${value.countryCode}${value.number}`);
        return phoneNumber
            .formatInternational()
            .replace(value.countryCode, '')
            .trim();
    }
    getExtPrefix(country) {
        return getExtPrefix(country, this.metadata);
    }
}
PhoneService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PhoneService, deps: [{ token: PhoneMetadataService }], target: i0.ɵɵFactoryTarget.Injectable });
PhoneService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PhoneService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: PhoneService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: PhoneMetadataService }]; } });

class FsPhoneFieldDeprecatedComponent {
    constructor(ngControl, _fb, _fm, _el, _phone, _countriesStore, _metadata, _phoneConfig) {
        this.ngControl = ngControl;
        this._fb = _fb;
        this._fm = _fm;
        this._el = _el;
        this._phone = _phone;
        this._countriesStore = _countriesStore;
        this._metadata = _metadata;
        this._phoneConfig = _phoneConfig;
        this.allowNumberExt = false;
        this.mode = 'string';
        this.autocomplete = 'on';
        this.id = `example-tel-input-${FsPhoneFieldDeprecatedComponent.nextId++}`;
        this.focused = false;
        this.touched = false;
        this.controlType = 'phone-input';
        this.stateChanges = new Subject();
        this.extPrefix = '';
        this.countryControl = new FormControl('');
        this._externalDataReady = false;
        this._required = false;
        this._disabled = false;
        this._writeValue$ = new ReplaySubject(1);
        this._containerClick = new Subject();
        this._countrySelectOpened$ = new Subject();
        this._destroy$ = new Subject();
        // Value Accessor
        this._onTouched = () => { };
        this._onChange = () => { };
        this._initControls();
        this._registerValueAccessor();
        this._listenContainerClick();
        this._initResourcesReadyState();
    }
    set placeholder(placeholder) {
        this._placeholder = placeholder;
        this.stateChanges.next();
    }
    set required(value) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        if (this._disabled) {
            this.phoneNumberParts.disable();
            this.countryControl.disable();
        }
        else {
            this.phoneNumberParts.enable();
            this.countryControl.enable();
        }
        this.stateChanges.next();
    }
    get countries$() {
        return this._countriesStore.countries$;
    }
    get phoneNumberEl() {
        return this._phoneNumberInputRef.nativeElement;
    }
    get extNumberEl() {
        return this._extNumberInputRef.nativeElement;
    }
    get emojiSupported() {
        return this._countriesStore.emojiSupported;
    }
    get errorState() {
        return this.ngControl.dirty && !this.ngControl.valid;
    }
    get value() {
        var _a;
        let value = this.phoneNumberParts.value;
        if (this.mode === 'string') {
            // const phoneNumber = value.number.replace(/[^0-9.]/g, '');
            let phoneNumberString = `${value.countryCode} ${value.number}`;
            if (value.ext && this.countryControl.value) {
                phoneNumberString += ` ${this._phone.getExtPrefix(this.countryControl.value)} ${value.ext}`;
            }
            return phoneNumberString;
        }
        if (this.countryControl.value) {
            const country = this._countriesStore.countryByISOCode(this.countryControl.value);
            value = {
                countryCode: value.countryCode.replace('+', ''),
                ext: value.ext,
                number: (_a = value.number) === null || _a === void 0 ? void 0 : _a.replace(/[^0-9]/g, ''),
                isoCode: country.isoCode,
                emoji: country.emoji,
            };
        }
        return value;
    }
    get placeholder() {
        return this._placeholder;
    }
    get required() {
        return this._required;
    }
    get disabled() {
        return this._disabled;
    }
    get shouldLabelFloat() {
        return true;
    }
    get empty() {
        if (this.mode === 'object') {
            return !this.codeValue && !this.numberValue && this._externalDataReady;
        }
        return !this.value && this._externalDataReady;
    }
    get codeValue() {
        return this.phoneNumberParts.get('countryCode').value;
    }
    get numberValue() {
        return this.phoneNumberParts.get('number').value;
    }
    get extValue() {
        return this.phoneNumberParts.get('ext').value;
    }
    get countryIsoCodeValue() {
        return this.countryControl.value;
    }
    ngOnInit() {
        this._initDefaultCountry();
        this._applyInternalValidation();
        this._listenWriteValue();
        this._listenPhonePartsChanges();
        this._listenCountryChanges();
        this._applyMaterialHacks();
    }
    ngOnDestroy() {
        this._fm.stopMonitoring(this._el);
        this.stateChanges.complete();
    }
    formatInput() {
        if (this.phoneNumberEl) {
            const asYouType = new AsYouType(this.countryControl.value);
            this.phoneNumberEl.value = asYouType.input(this.phoneNumberEl.value);
        }
    }
    phonePaste() {
        setTimeout(() => {
            this.phoneFormat();
        });
    }
    phoneFormat() {
        const phoneNumber = parsePhoneNumberFromString(this.phoneNumberEl.value, this.countryControl.value);
        if (phoneNumber) {
            phoneNumber.ext = this.extValue;
            this.phoneNumberEl.value = phoneNumber.formatInternational();
            this._setPhoneNumber(phoneNumber);
        }
    }
    phoneKeydown(event) {
        if (this.allowNumberExt &&
            event.code === 'ArrowRight' &&
            this.phoneNumberEl.selectionStart === this.phoneNumberEl.value.length) {
            this.focusExtNumber();
            return;
        }
    }
    phoneKeypress(event) {
        const regEx = new RegExp(/[^\d\)\(\s\-)]/, 'g');
        if (event.key && event.key.match(regEx)) {
            event.preventDefault();
        }
    }
    phoneKeyup(event) {
        if (!event.key.match(/[\d]/) &&
            (event.code === 'Backspace' ||
                event.code === 'Delete' ||
                event.key === 'Tab' ||
                event.key === 'Control' ||
                event.key === 'Shift' ||
                event.key === 'Meta' ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey)) {
            return;
        }
        try {
            const input = this.phoneNumberEl;
            const value = input.value;
            const asYouType = new AsYouType(this.countryControl.value);
            const formatted = asYouType.input(value);
            if (input.value.length === input.selectionStart) {
                if (event.code === 'Backspace' && !asYouType.isValid()) {
                    return;
                }
                input.value = formatted;
                if (asYouType.isValid()) {
                    this.phoneFormat();
                }
            }
            if (this.allowNumberExt &&
                event.key.match(/\d/) &&
                ['US', 'CA'].indexOf(this.countryIsoCodeValue) !== -1 &&
                asYouType.isValid()) {
                this.selectExtNumber();
            }
        }
        catch (error) {
        }
    }
    focusExtNumber() {
        setTimeout(() => {
            this.extNumberEl.focus();
        });
    }
    selectExtNumber() {
        setTimeout(() => {
            this.extNumberEl.select();
        });
    }
    focusPhoneNumber() {
        setTimeout(() => {
            this.phoneNumberEl.focus();
        });
    }
    extKeyup(event) {
        if (event.code === 'Backspace') {
            if (this.extNumberEl.selectionStart === 0) {
                this.focusPhoneNumber();
            }
        }
    }
    extKeydown(event) {
        if (event.code === 'ArrowLeft' &&
            this.extNumberEl.selectionStart === 0) {
            this.focusPhoneNumber();
            return;
        }
    }
    onContainerClick(event) {
        this._containerClick.next(event.target);
    }
    countrySelectOpened() {
        this._countrySelectOpened$.next();
    }
    setDescribedByIds(ids) { }
    writeValue(value) {
        this._writeValue$.next(value);
    }
    registerOnChange(onChange) {
        this._onChange = onChange;
    }
    registerOnTouched(onTouched) {
        this._onTouched = onTouched;
    }
    validate({ value }) {
        const validationErrors = {};
        let isNotValid = false;
        if (this.required) {
            const hasValue = this.codeValue && this.numberValue;
            if (!hasValue) {
                isNotValid = true;
                validationErrors.required = true;
            }
        }
        if (this.codeValue && this.numberValue) {
            if (!this._phone.isPhoneNumberValid(this.phoneNumberParts.value)) {
                isNotValid = true;
                validationErrors.invalid = 'Invalid number';
            }
        }
        return isNotValid && validationErrors;
    }
    onFocusIn(event) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }
    onFocusOut(event) {
        if (!this._el.nativeElement.contains(event.relatedTarget)) {
            this.touched = true;
            this.focused = false;
            this._onTouched();
            this.stateChanges.next();
        }
    }
    countrySelectionChange() {
        this.formatInput();
    }
    _registerValueAccessor() {
        if (this.ngControl != null) {
            // Setting the value accessor directly (instead of using
            // the providers) to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
    }
    _listenContainerClick() {
        this._containerClick
            .pipe(filter(() => !this.disabled), filter((target) => {
            return target.tagName.toLowerCase() !== 'input';
        }), switchMap(() => merge(timer(200).pipe(mapTo(false)), this._countrySelectOpened$.pipe(mapTo(true))).pipe(take(1))), tap((isSelectClick) => {
            if (!isSelectClick) {
                this._fm.focusVia(this._phoneNumberInputRef, 'program');
            }
        }), takeUntil(this._destroy$))
            .subscribe();
    }
    _initDefaultCountry() {
        var _a;
        this.country = this.country || ((_a = this._phoneConfig) === null || _a === void 0 ? void 0 : _a.isoCountryCode);
    }
    _initControls() {
        this.phoneNumberParts = this._fb.group({
            countryCode: '',
            number: '',
            ext: '',
        });
    }
    _initResourcesReadyState() {
        this.ready$ = combineLatest([this._countriesStore.ready$, this._metadata.ready$])
            .pipe(map(([countriesReady, metadataReady]) => {
            return countriesReady && metadataReady;
        }), tap((state) => {
            this._externalDataReady = state;
            this.stateChanges.next();
        }), shareReplay(1));
    }
    _listenPhonePartsChanges() {
        this.phoneNumberParts.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            if (this.empty) {
                this._onChange(null);
            }
            else {
                this._onChange(this.value);
            }
            this.stateChanges.next();
        });
    }
    _listenCountryChanges() {
        this.countryControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this._destroy$))
            .subscribe((countryCode) => {
            this._setCountryCode(countryCode);
        });
    }
    /**
     * mat form field underline must have auto width
     */
    _applyMaterialHacks() {
        this._el.nativeElement.parentElement.style.width = 'auto';
    }
    /**
     * Required and Invalid validators are custom
     */
    _applyInternalValidation() {
        if (this.ngControl) {
            this.ngControl.control.setValidators([this.validate.bind(this)]);
        }
    }
    _updateExt(code) {
        this.extPrefix = this._phone.getExtPrefix(code);
    }
    _listenWriteValue() {
        this.ready$
            .pipe(filter((state) => !!state), switchMap(() => {
            return this._writeValue$;
        }))
            .subscribe((value) => {
            this._writeValue(value);
            if (!value) {
                this._initWithDefaultCountry();
            }
        });
    }
    _writeValue(value) {
        let phoneNumber;
        // In case when string received we must parse it before continue
        // otherwise we should transform passed object into PhoneNumber instance
        if (typeof value === 'string' || typeof value === 'number') {
            if (typeof value === 'number') {
                value = value.toString();
            }
            try {
                phoneNumber = this._phone.parsePhoneNumber(value, this.country);
            }
            catch (e) { }
        }
        else if (value && typeof value === 'object') {
            if (value.isoCode) {
                this.country = value.isoCode;
            }
            phoneNumber = this._phone.parsePhoneNumber(`+${value.countryCode}${value.number.toString()}`, this.country);
            if (phoneNumber) {
                phoneNumber.ext = value.ext;
            }
        }
        // If transformed to PhoneNumber correctly
        if (phoneNumber) {
            if (!phoneNumber.isPossible()) {
                throw Error('Invalid phone number');
            }
            this._setPhoneNumber(phoneNumber, false);
        }
        else {
            this.phoneNumberParts.reset({
                code: '',
                number: '',
                ext: '',
            }, { emitEvent: false });
            this._initWithDefaultCountry();
        }
    }
    _setPhoneNumber(phoneNumber, emitEvent = true) {
        const parsedNumber = this._phone.formatIncompletePhoneNumber(`+${phoneNumber.countryCallingCode}`, phoneNumber.nationalNumber, phoneNumber.country);
        this.phoneNumberParts.patchValue({
            countryCode: phoneNumber.countryCallingCode || '',
            number: parsedNumber,
            ext: phoneNumber.ext || '',
        }, { emitEvent });
        this._setCountryCode(phoneNumber.country, emitEvent);
    }
    _setCountryCode(isoCode, emitEvent = true) {
        const country = this._countriesStore.countryByISOCode(isoCode);
        this.countryControl.patchValue(isoCode, { emitEvent });
        this._updateExt(isoCode);
        const data = country
            ? { countryCode: `+${country.countryCode}` }
            : { countryCode: '' };
        this.phoneNumberParts.patchValue(data, { emitEvent });
    }
    _initWithDefaultCountry() {
        this.countryControl.setValue(this.country, { emitEvent: false });
        this._setCountryCode(this.country, false);
    }
}
FsPhoneFieldDeprecatedComponent.nextId = 0;
FsPhoneFieldDeprecatedComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneFieldDeprecatedComponent, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i1.FormBuilder }, { token: i2.FocusMonitor }, { token: i0.ElementRef }, { token: PhoneService }, { token: i4.FsCountry }, { token: PhoneMetadataService }, { token: PHONE_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Component });
FsPhoneFieldDeprecatedComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsPhoneFieldDeprecatedComponent, selector: "fs-phone-field", inputs: { allowNumberExt: "allowNumberExt", placeholder: "placeholder", required: "required", disabled: "disabled", mode: "mode", country: "country", name: "name", autocomplete: "autocomplete" }, host: { properties: { "class.with-number-extention": "this.allowNumberExt", "id": "this.id", "class.floating": "this.shouldLabelFloat" } }, providers: [
        { provide: MatFormFieldControl, useExisting: FsPhoneFieldDeprecatedComponent },
    ], viewQueries: [{ propertyName: "_extNumberInputRef", first: true, predicate: ["extNumberInput"], descendants: true }, { propertyName: "_phoneNumberInputRef", first: true, predicate: ["phoneNumberInput"], descendants: true }], ngImport: i0, template: "<ng-container *fsSkeleton=\"ready$ | async; pattern: [100]\">\n  <div class=\"phone-group\" role=\"group\"\n       [class.with-emoji]=\"emojiSupported\"\n       (focusin)=\"onFocusIn($event)\"\n       (focusout)=\"onFocusOut($event)\">\n    <mat-select\n        class=\"phone-code\"\n        (opened)=\"countrySelectOpened()\"\n        (selectionChange)=\"countrySelectionChange()\"\n        [formControl]=\"countryControl\"\n        tabindex=\"-1\"\n        panelClass=\"fs-phone-country-pane\"\n        #countryInput>\n      <mat-select-trigger>\n        <fs-country-phone-code\n          [code]=\"countryControl.value\"\n          [showCode]=\"false\">\n        </fs-country-phone-code>\n      </mat-select-trigger>\n\n      <ng-container *ngFor=\"let country of countries$ | async\">\n        <mat-option\n            *ngIf=\"!!country.countryCode\"\n            [value]=\"country.isoCode\">\n          {{ country.name }}\n          <span class=\"fs-phone-country-code\">&nbsp;+{{ country.countryCode }}&nbsp;</span>\n          <fs-country-flag\n            class=\"fs-phone-country-flag\"\n            [code]=\"country.isoCode\">\n          </fs-country-flag>\n        </mat-option>\n      </ng-container>\n    </mat-select>\n\n    <ng-container [formGroup]=\"phoneNumberParts\">\n      <span class=\"code\" [class.disabled]=\"disabled\">{{codeValue}}</span>\n      <input\n        class=\"phone-number\"\n        matInput\n        [name]=\"name\"\n        placeholder=\"Phone\"\n        [autocomplete]=\"autocomplete\"\n        formControlName=\"number\"\n        (keyup)=\"phoneKeyup($event)\"\n        (keydown)=\"phoneKeydown($event)\"\n        (keypress)=\"phoneKeypress($event)\"\n        (paste)=\"phonePaste()\"\n        (blur)=\"phoneFormat()\"\n        #phoneNumberInput>\n\n      <ng-container *ngIf=\"allowNumberExt && extPrefix\">\n        <span class=\"ext-text\">{{ extPrefix }}</span>\n        <input\n          #extNumberInput\n          (keyup)=\"extKeyup($event)\"\n          (keydown)=\"extKeydown($event)\"\n          class=\"ext-number\"\n          matInput\n          placeholder=\"xxx\"\n          formControlName=\"ext\"\n          [name]=\"name + '-ext'\">\n      </ng-container>\n    </ng-container>\n  </div>\n</ng-container>\n", styles: [":host{display:block;width:auto}:host ::ng-deep input.mat-input-element{margin-top:0}::ng-deep .fs-phone-country-pane mat-select-trigger{position:relative}::ng-deep .fs-phone-country-pane mat-select-trigger fs-country-phone-code{position:relative;top:1px}::ng-deep .fs-phone-country-pane .mat-option-text{display:inline-flex}::ng-deep .fs-phone-country-pane .mat-option-text .fs-phone-country-code{order:-1}::ng-deep .fs-phone-country-pane .mat-option-text .fs-phone-country-flag{order:-2}.code{margin-right:5px}.disabled{opacity:.38}.phone-group{display:flex;align-items:center}.phone-group.with-emoji{align-items:baseline}.phone-group .phone-code{min-width:38px;width:auto}.phone-group .phone-number{width:110px;min-width:110px;margin-left:4px}.phone-group .ext-text{margin-left:10px}.phone-group .ext-number{margin-left:5px;width:50px}:host(:not(.floating)) ::ng-deep .mat-select-arrow-wrapper .mat-select-arrow{opacity:0!important}\n"], components: [{ type: i6.MatSelect, selector: "mat-select", inputs: ["disabled", "disableRipple", "tabIndex"], exportAs: ["matSelect"] }, { type: i4.FsCountryPhoneCodeComponent, selector: "fs-country-phone-code", inputs: ["showCode"] }, { type: i7.MatOption, selector: "mat-option", exportAs: ["matOption"] }, { type: i4.FsCountryFlagComponent, selector: "fs-country-flag", inputs: ["code", "width"] }], directives: [{ type: i8.FsSkeletonContentDirective, selector: "[fsSkeleton]", inputs: ["fsSkeleton", "fsSkeletonPattern"] }, { type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.FormControlDirective, selector: "[formControl]", inputs: ["disabled", "formControl", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { type: i6.MatSelectTrigger, selector: "mat-select-trigger" }, { type: i9.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i7$1.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["id", "disabled", "required", "type", "value", "readonly", "placeholder", "errorStateMatcher", "aria-describedby"], exportAs: ["matInput"] }, { type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i1.FormControlName, selector: "[formControlName]", inputs: ["disabled", "formControlName", "ngModel"], outputs: ["ngModelChange"] }], pipes: { "async": i9.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneFieldDeprecatedComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-phone-field',
                    templateUrl: './phone-field-deprecated.component.html',
                    styleUrls: ['./phone-field-deprecated.component.scss'],
                    providers: [
                        { provide: MatFormFieldControl, useExisting: FsPhoneFieldDeprecatedComponent },
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i1.FormBuilder }, { type: i2.FocusMonitor }, { type: i0.ElementRef }, { type: PhoneService }, { type: i4.FsCountry }, { type: PhoneMetadataService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [PHONE_CONFIG]
                }] }]; }, propDecorators: { allowNumberExt: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.with-number-extention']
            }], placeholder: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], mode: [{
                type: Input
            }], country: [{
                type: Input
            }], name: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], id: [{
                type: HostBinding
            }], _extNumberInputRef: [{
                type: ViewChild,
                args: ['extNumberInput']
            }], _phoneNumberInputRef: [{
                type: ViewChild,
                args: ['phoneNumberInput']
            }], shouldLabelFloat: [{
                type: HostBinding,
                args: ['class.floating']
            }] } });

class FsPhoneFieldComponent {
    constructor(ngControl, _phoneConfig, _fb, _fm, _el, _phone, _countriesStore, _metadata, _formField, _matInput) {
        this.ngControl = ngControl;
        this._phoneConfig = _phoneConfig;
        this._fb = _fb;
        this._fm = _fm;
        this._el = _el;
        this._phone = _phone;
        this._countriesStore = _countriesStore;
        this._metadata = _metadata;
        this._formField = _formField;
        this._matInput = _matInput;
        this.allowNumberExt = false;
        this.mode = 'string';
        this.autocomplete = 'on';
        this.id = `example-tel-input-${FsPhoneFieldComponent.nextId++}`;
        this.focused = false;
        this.touched = false;
        this.controlType = 'phone-input';
        this.stateChanges = new Subject();
        this.extPrefix = '';
        this.countryControl = new FormControl('');
        this._externalDataReady = false;
        this._required = false;
        this._disabled = false;
        this._writeValue$ = new ReplaySubject(1);
        this._containerClick = new Subject();
        this._countrySelectOpened$ = new Subject();
        this._destroy$ = new Subject();
        this._initControls();
        this._registerValueAccessor();
        this._listenContainerClick();
        this._initResourcesReadyState();
    }
    set placeholder(placeholder) {
        this._placeholder = placeholder;
        this.stateChanges.next();
    }
    set required(value) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        if (this._disabled) {
            this.phoneNumberParts.disable();
            this.countryControl.disable();
        }
        else {
            this.phoneNumberParts.enable();
            this.countryControl.enable();
        }
        this.stateChanges.next();
    }
    ngAfterContentInit() {
        const portal = new DomPortal(this._el.nativeElement);
        const el = this._el;
        el.nativeElement.childNodes.forEach((c) => {
            el.nativeElement.offsetParent.append(c);
        });
        this.selectedPortal = portal;
    }
    get countries$() {
        return this._countriesStore.countries$;
    }
    get phoneNumberEl() {
        return this._el.nativeElement;
    }
    get extNumberEl() {
        return this._extNumberInputRef.nativeElement;
    }
    get emojiSupported() {
        return this._countriesStore.emojiSupported;
    }
    get errorState() {
        return this.ngControl.dirty && !this.ngControl.valid;
    }
    get value() {
        var _a;
        let value = Object.assign(Object.assign({}, this.phoneNumberParts.value), { number: this._matInput.value });
        if (this.mode === 'string') {
            // const phoneNumber = value.number.replace(/[^0-9.]/g, '');
            let phoneNumberString = `${value.countryCode} ${value.number}`;
            if (value.ext && this.countryControl.value) {
                phoneNumberString += ` ${this._phone.getExtPrefix(this.countryControl.value)} ${value.ext}`;
            }
            return phoneNumberString;
        }
        if (this.countryControl.value) {
            const country = this._countriesStore.countryByISOCode(this.countryControl.value);
            value = {
                countryCode: value.countryCode.replace('+', ''),
                ext: value.ext,
                number: (_a = value.number) === null || _a === void 0 ? void 0 : _a.replace(/[^0-9]/g, ''),
                isoCode: country.isoCode,
                emoji: country.emoji,
            };
        }
        return value;
    }
    get placeholder() {
        return this._placeholder;
    }
    get required() {
        return this._required;
    }
    get disabled() {
        return this._disabled;
    }
    get shouldLabelFloat() {
        return true;
    }
    get empty() {
        if (this.mode === 'object') {
            return !this.codeValue && !this.numberValue && this._externalDataReady;
        }
        return !this.value && this._externalDataReady;
    }
    get codeValue() {
        return this.phoneNumberParts.get('countryCode').value;
    }
    get numberValue() {
        return this._matInput.value;
    }
    get extValue() {
        return this.phoneNumberParts.get('ext').value;
    }
    get countryIsoCodeValue() {
        return this.countryControl.value;
    }
    ngOnInit() {
        this._initDefaultCountry();
        this._applyInternalValidation();
        this._listenWriteValue();
        this._listenPhonePartsChanges();
        this._listenCountryChanges();
        this._applyMaterialHacks();
        this._initEvents();
    }
    ngOnDestroy() {
        this._fm.stopMonitoring(this._el);
        this.stateChanges.complete();
    }
    formatInput() {
        if (this.phoneNumberEl) {
            const asYouType = new AsYouType(this.countryControl.value);
            this.phoneNumberEl.value = asYouType.input(this.phoneNumberEl.value);
        }
    }
    phonePaste() {
        setTimeout(() => {
            this.phoneFormat();
        });
    }
    phoneFormat() {
        const phoneNumber = parsePhoneNumberFromString(this.phoneNumberEl.value, this.countryControl.value);
        if (phoneNumber) {
            phoneNumber.ext = this.extValue;
            this.phoneNumberEl.value = phoneNumber.formatInternational();
            this._setPhoneNumber(phoneNumber);
        }
    }
    phoneKeydown(event) {
        if (this.allowNumberExt &&
            event.code === 'ArrowRight' &&
            this.phoneNumberEl.selectionStart === this.phoneNumberEl.value.length) {
            this.focusExtNumber();
            return;
        }
    }
    phoneKeypress(event) {
        const regEx = new RegExp(/[^\d\)\(\s\-)]/, 'g');
        if (event.key && event.key.match(regEx)) {
            event.preventDefault();
        }
    }
    phoneKeyup(event) {
        if (!event.key.match(/[\d]/) &&
            (event.code === 'Backspace' ||
                event.code === 'Delete' ||
                event.key === 'Tab' ||
                event.key === 'Control' ||
                event.key === 'Shift' ||
                event.key === 'Meta' ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey)) {
            return;
        }
        try {
            const input = this.phoneNumberEl;
            const value = input.value;
            const asYouType = new AsYouType(this.countryControl.value);
            const formatted = asYouType.input(value);
            if (input.value.length === input.selectionStart) {
                if (event.code === 'Backspace' && !asYouType.isValid()) {
                    return;
                }
                input.value = formatted;
                if (asYouType.isValid()) {
                    this.phoneFormat();
                }
            }
            if (this.allowNumberExt &&
                event.key.match(/\d/) &&
                ['US', 'CA'].indexOf(this.countryIsoCodeValue) !== -1 &&
                asYouType.isValid()) {
                this.selectExtNumber();
            }
        }
        catch (error) {
        }
    }
    focusExtNumber() {
        setTimeout(() => {
            this.extNumberEl.focus();
        });
    }
    selectExtNumber() {
        setTimeout(() => {
            this.extNumberEl.select();
        });
    }
    focusPhoneNumber() {
        setTimeout(() => {
            this.phoneNumberEl.focus();
        });
    }
    extKeyup(event) {
        if (event.code === 'Backspace') {
            if (this.extNumberEl.selectionStart === 0) {
                this.focusPhoneNumber();
            }
        }
    }
    extKeydown(event) {
        if (event.code === 'ArrowLeft' &&
            this.extNumberEl.selectionStart === 0) {
            this.focusPhoneNumber();
            return;
        }
    }
    onContainerClick(event) {
        this._containerClick.next(event.target);
    }
    countrySelectOpened() {
        this._countrySelectOpened$.next();
    }
    setDescribedByIds(ids) { }
    writeValue(value) {
        this._writeValue$.next(value);
    }
    registerOnChange(onChange) {
        this._onChange = onChange;
    }
    registerOnTouched(onTouched) {
        this._onTouched = onTouched;
    }
    validate({ value }) {
        const validationErrors = {};
        let isNotValid = false;
        if (this.required) {
            const hasValue = this.codeValue && this.numberValue;
            if (!hasValue) {
                isNotValid = true;
                validationErrors.required = true;
            }
        }
        if (this.codeValue && this.numberValue) {
            if (!this._phone.isPhoneNumberValid(this.value)) {
                isNotValid = true;
                validationErrors.invalid = 'Invalid number';
            }
        }
        return isNotValid && validationErrors;
    }
    onFocusIn(event) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }
    onFocusOut(event) {
        if (!this._el.nativeElement.contains(event.relatedTarget)) {
            this.touched = true;
            this.focused = false;
            this._onTouched();
            this.stateChanges.next();
        }
    }
    countrySelectionChange() {
        this.formatInput();
    }
    _registerValueAccessor() {
        if (this.ngControl !== null) {
            // Setting the value accessor directly (instead of using
            // the providers) to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
    }
    _listenContainerClick() {
        this._containerClick
            .pipe(filter(() => !this.disabled), filter((target) => {
            return target.tagName.toLowerCase() !== 'input';
        }), switchMap(() => merge(timer(200).pipe(mapTo(false)), this._countrySelectOpened$.pipe(mapTo(true))).pipe(take(1))), tap((isSelectClick) => {
            if (!isSelectClick) {
                this._fm.focusVia(this._el, 'program');
            }
        }), takeUntil(this._destroy$))
            .subscribe();
    }
    _initDefaultCountry() {
        var _a;
        this.country = this.country || ((_a = this._phoneConfig) === null || _a === void 0 ? void 0 : _a.isoCountryCode);
    }
    _initControls() {
        this._formField.floatLabel = 'always';
        this.phoneNumberParts = this._fb.group({
            countryCode: '',
            //number: '',
            ext: '',
        });
    }
    _initResourcesReadyState() {
        this.ready$ = combineLatest([this._countriesStore.ready$, this._metadata.ready$])
            .pipe(map(([countriesReady, metadataReady]) => {
            return countriesReady && metadataReady;
        }), tap((state) => {
            this._externalDataReady = state;
            this.stateChanges.next();
        }), shareReplay(1));
    }
    _listenPhonePartsChanges() {
        merge(this.phoneNumberParts.valueChanges)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._phonePartsChange();
        });
        fromEvent(this.phoneNumberEl, 'input')
            .pipe(takeUntil(this._destroy$))
            .subscribe((event) => {
            this._phonePartsChange();
        });
    }
    _phonePartsChange() {
        if (this.empty) {
            this._onChange(null);
        }
        else {
            this._onChange(this.value);
        }
        this.stateChanges.next();
    }
    _listenCountryChanges() {
        this.countryControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this._destroy$))
            .subscribe((countryCode) => {
            this._setCountryCode(countryCode);
        });
    }
    /**
     * mat form field underline must have auto width
     */
    _applyMaterialHacks() {
        this._el.nativeElement.parentElement.style.width = 'auto';
    }
    _initEvents() {
        merge(fromEvent(this.phoneNumberEl, 'keyup'), fromEvent(this.phoneNumberEl, 'keydown'), fromEvent(this.phoneNumberEl, 'keypress'), fromEvent(this.phoneNumberEl, 'paste'), fromEvent(this.phoneNumberEl, 'blur'))
            .pipe(takeUntil(this._destroy$))
            .subscribe((event) => {
            switch (event.type) {
                case 'keyup':
                    return this.phoneKeyup(event);
                case 'keydown':
                    return this.phoneKeydown(event);
                case 'keypress':
                    return this.phoneKeypress(event);
                case 'paste':
                    return this.phonePaste();
                case 'blur':
                    return this.phoneFormat();
            }
        });
    }
    /**
     * Required and Invalid validators are custom
     */
    _applyInternalValidation() {
        if (this.ngControl) {
            this.ngControl.control.setValidators([this.validate.bind(this)]);
        }
    }
    _updateExt(code) {
        this.extPrefix = this._phone.getExtPrefix(code);
    }
    _listenWriteValue() {
        this.ready$
            .pipe(filter((state) => !!state), switchMap(() => {
            return this._writeValue$;
        }), takeUntil(this._destroy$))
            .subscribe((value) => {
            this._writeValue(value);
            if (!value) {
                this._initWithDefaultCountry();
            }
        });
    }
    _writeValue(value) {
        let phoneNumber;
        // In case when string received we must parse it before continue
        // otherwise we should transform passed object into PhoneNumber instance
        if (typeof value === 'string' || typeof value === 'number') {
            if (typeof value === 'number') {
                value = value.toString();
            }
            try {
                phoneNumber = this._phone.parsePhoneNumber(value, this.country);
            }
            catch (e) { }
        }
        else if (value && typeof value === 'object') {
            if (value.isoCode) {
                this.country = value.isoCode;
            }
            phoneNumber = this._phone.parsePhoneNumber(`+${value.countryCode}${value.number.toString()}`, this.country);
            if (phoneNumber) {
                phoneNumber.ext = value.ext;
            }
        }
        // If transformed to PhoneNumber correctly
        if (phoneNumber) {
            if (!phoneNumber.isPossible()) {
                throw Error('Invalid phone number');
            }
            this._setPhoneNumber(phoneNumber, false);
        }
        else {
            this.phoneNumberParts.reset({
                code: '',
                number: '',
                ext: '',
            }, { emitEvent: false });
            this._initWithDefaultCountry();
        }
    }
    _setPhoneNumber(phoneNumber, emitEvent = true) {
        const parsedNumber = this._phone.formatIncompletePhoneNumber(`+${phoneNumber.countryCallingCode}`, phoneNumber.nationalNumber, phoneNumber.country);
        this.phoneNumberParts.patchValue({
            countryCode: phoneNumber.countryCallingCode || '',
            //number: parsedNumber,
            ext: phoneNumber.ext || '',
        }, { emitEvent });
        this._matInput.value = parsedNumber;
        this._setCountryCode(phoneNumber.country, emitEvent);
    }
    _setCountryCode(isoCode, emitEvent = true) {
        const country = this._countriesStore.countryByISOCode(isoCode);
        this.countryControl.patchValue(isoCode, { emitEvent });
        this._updateExt(isoCode);
        const data = country
            ? { countryCode: `+${country.countryCode}` }
            : { countryCode: '' };
        this.phoneNumberParts.patchValue(data, { emitEvent });
    }
    _initWithDefaultCountry() {
        this.countryControl.setValue(this.country, { emitEvent: false });
        this._setCountryCode(this.country, false);
    }
}
FsPhoneFieldComponent.nextId = 0;
FsPhoneFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneFieldComponent, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: PHONE_CONFIG, optional: true }, { token: i1.FormBuilder }, { token: i2.FocusMonitor }, { token: i0.ElementRef }, { token: PhoneService }, { token: i4.FsCountry }, { token: PhoneMetadataService }, { token: i6$1.MatFormField }, { token: i7$1.MatInput }], target: i0.ɵɵFactoryTarget.Component });
FsPhoneFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsPhoneFieldComponent, selector: "[fsPhoneField]", inputs: { allowNumberExt: "allowNumberExt", placeholder: "placeholder", required: "required", disabled: "disabled", mode: "mode", country: "country", autocomplete: "autocomplete" }, host: { properties: { "class.with-number-extention": "this.allowNumberExt", "id": "this.id", "class.floating": "this.shouldLabelFloat" } }, providers: [
        { provide: MatFormFieldControl, useExisting: FsPhoneFieldComponent },
    ], viewQueries: [{ propertyName: "_extNumberInputRef", first: true, predicate: ["extNumberInput"], descendants: true }], ngImport: i0, template: "<div \n    class=\"phone-group\" \n    role=\"group\"\n    [class.with-emoji]=\"emojiSupported\"\n    (focusin)=\"onFocusIn($event)\"\n    (focusout)=\"onFocusOut($event)\">\n  <mat-select\n      class=\"phone-code\"\n      (opened)=\"countrySelectOpened()\"\n      (selectionChange)=\"countrySelectionChange()\"\n      [formControl]=\"countryControl\"\n      tabindex=\"-1\"\n      panelClass=\"fs-phone-country-pane\"\n      #countryInput>\n    <mat-select-trigger>\n      <fs-country-phone-code\n        [code]=\"countryControl.value\"\n        [showCode]=\"false\">\n      </fs-country-phone-code>\n    </mat-select-trigger>\n\n    <ng-container *ngFor=\"let country of countries$ | async\">\n      <mat-option\n          *ngIf=\"!!country.countryCode\"\n          [value]=\"country.isoCode\">\n          {{ country.name }}\n        <span class=\"fs-phone-country-code\">&nbsp;+{{ country.countryCode }}&nbsp;</span>\n        <fs-country-flag\n          class=\"fs-phone-country-flag\"\n          [code]=\"country.isoCode\">\n        </fs-country-flag>\n      </mat-option>\n    </ng-container>\n  </mat-select>\n\n  <ng-container [formGroup]=\"phoneNumberParts\">\n    <span class=\"code\" [class.disabled]=\"disabled\">{{codeValue}}</span>\n    <span>\n      <ng-template [cdkPortalOutlet]=\"selectedPortal\"></ng-template>\n    </span>\n    <ng-container *ngIf=\"allowNumberExt && extPrefix\">\n      <span class=\"ext-text\">{{ extPrefix }}</span>\n      <input\n        #extNumberInput\n        (keyup)=\"extKeyup($event)\"\n        (keydown)=\"extKeydown($event)\"\n        class=\"ext-number\"\n        matInput\n        placeholder=\"xxx\"\n        formControlName=\"ext\">\n    </ng-container>\n  </ng-container>\n</div>\n", styles: [":host{display:block;width:auto}:host ::ng-deep input.mat-input-element{margin-top:0}::ng-deep .fs-phone-country-pane mat-select-trigger{position:relative}::ng-deep .fs-phone-country-pane mat-select-trigger fs-country-phone-code{position:relative;top:1px}::ng-deep .fs-phone-country-pane .mat-option-text{display:inline-flex}::ng-deep .fs-phone-country-pane .mat-option-text .fs-phone-country-code{order:-1}::ng-deep .fs-phone-country-pane .mat-option-text .fs-phone-country-flag{order:-2}.code{margin-right:5px;margin-top:-.0625em}.disabled{opacity:.38}.phone-group{display:flex;align-items:center}.phone-group.with-emoji{align-items:baseline}.phone-group .phone-code{min-width:38px;width:auto}.phone-group .phone-number{width:110px;min-width:110px;margin-left:4px}.phone-group .ext-text{margin-left:10px}.phone-group .ext-number{margin-left:5px;width:50px}:host(:not(.floating)) ::ng-deep .mat-select-arrow-wrapper .mat-select-arrow{opacity:0!important}\n"], components: [{ type: i6.MatSelect, selector: "mat-select", inputs: ["disabled", "disableRipple", "tabIndex"], exportAs: ["matSelect"] }, { type: i4.FsCountryPhoneCodeComponent, selector: "fs-country-phone-code", inputs: ["showCode"] }, { type: i7.MatOption, selector: "mat-option", exportAs: ["matOption"] }, { type: i4.FsCountryFlagComponent, selector: "fs-country-flag", inputs: ["code", "width"] }], directives: [{ type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.FormControlDirective, selector: "[formControl]", inputs: ["disabled", "formControl", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { type: i6.MatSelectTrigger, selector: "mat-select-trigger" }, { type: i9.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i11.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { type: i7$1.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["id", "disabled", "required", "type", "value", "readonly", "placeholder", "errorStateMatcher", "aria-describedby"], exportAs: ["matInput"] }, { type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i1.FormControlName, selector: "[formControlName]", inputs: ["disabled", "formControlName", "ngModel"], outputs: ["ngModelChange"] }], pipes: { "async": i9.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsPhoneField]',
                    templateUrl: './phone-field.component.html',
                    styleUrls: ['./phone-field.component.scss'],
                    providers: [
                        { provide: MatFormFieldControl, useExisting: FsPhoneFieldComponent },
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [PHONE_CONFIG]
                }] }, { type: i1.FormBuilder }, { type: i2.FocusMonitor }, { type: i0.ElementRef }, { type: PhoneService }, { type: i4.FsCountry }, { type: PhoneMetadataService }, { type: i6$1.MatFormField }, { type: i7$1.MatInput }]; }, propDecorators: { allowNumberExt: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.with-number-extention']
            }], placeholder: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], mode: [{
                type: Input
            }], country: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], id: [{
                type: HostBinding
            }], _extNumberInputRef: [{
                type: ViewChild,
                args: ['extNumberInput']
            }], shouldLabelFloat: [{
                type: HostBinding,
                args: ['class.floating']
            }] } });

function formatPhoneNumber(str, options) {
    try {
        const phoneNumber = parsePhoneNumber(str);
        let formatted = '';
        if (((options === null || options === void 0 ? void 0 : options.format) || 'national') === 'national') {
            formatted = phoneNumber.formatNational();
        }
        else if ((options === null || options === void 0 ? void 0 : options.format) === 'international') {
            formatted = phoneNumber.formatNational();
        }
        return formatted.trim();
    }
    catch (e) {
        return str;
    }
}

class FsPhoneComponent {
    get formattedPhone() {
        return this._formattedPhone;
    }
    get phoneIsObject() {
        return typeof this.phone === 'object';
    }
    get phoneISOCode() {
        return this.phone.isoCode;
    }
    ngOnChanges(changes) {
        this._formattedPhone = this._parsePhone(this.phone);
    }
    _parsePhone(value) {
        let str;
        switch (typeof value) {
            case 'number':
                {
                    str = formatPhoneNumber('' + value);
                }
                break;
            case 'object':
                {
                    str = `+${value.countryCode} ${value.number}`;
                    if (value.ext) {
                        str = `${str} ext. ${value.ext}`;
                    }
                }
                break;
            default: {
                str = formatPhoneNumber(value);
            }
        }
        return str;
    }
}
FsPhoneComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsPhoneComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsPhoneComponent, selector: "fs-phone", inputs: { phone: "phone" }, usesOnChanges: true, ngImport: i0, template: "<fs-country-flag \n  *ngIf=\"phoneIsObject\"\n  [code]=\"phoneISOCode\">\n</fs-country-flag> {{ formattedPhone }}\n", components: [{ type: i4.FsCountryFlagComponent, selector: "fs-country-flag", inputs: ["code", "width"] }], directives: [{ type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-phone',
                    templateUrl: './phone.component.html',
                }]
        }], propDecorators: { phone: [{
                type: Input
            }] } });

class FsPhoneDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
    }
    onfocus() {
        const length = this._elementRef.nativeElement.value.length;
        setTimeout(() => {
            this._elementRef.nativeElement.setSelectionRange(length, length);
        }, 5);
    }
    input() {
        setTimeout(() => {
            this._onChange(this._imask.unmaskedValue);
        });
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    ngOnInit() {
        const maskOptions = {
            mask: '(000) 000-0000 ext. 00000',
        };
        this._imask = IMask(this._elementRef.nativeElement, maskOptions);
    }
    writeValue(value) {
        this._imask.value = toString(value);
    }
}
FsPhoneDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsPhoneDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsPhoneDirective, selector: "[fsPhone]", host: { listeners: { "focus": "onfocus()", "input": "input()" } }, providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FsPhoneDirective),
            multi: true,
        }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsPhone]',
                    providers: [{
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FsPhoneDirective),
                            multi: true,
                        }],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { onfocus: [{
                type: HostListener,
                args: ['focus']
            }], input: [{
                type: HostListener,
                args: ['input']
            }] } });

class FsPhonePipe {
    // private _value: number | string | IPhoneValue;
    // private _displayValue = '';
    // private _ready = this._phoneMetadataService.ready;
    // private _transform$ = new ReplaySubject<number |string | IPhoneValue>(1)
    // private _destroy$ = new Subject<void>();
    /*constructor(
      private _cdRef: ChangeDetectorRef,
      private _phoneService: PhoneService,
      private _phoneMetadataService: PhoneMetadataService,
      private _countryService: FsCountry,
    ) {
      // this._listenTransformStream();
    }*/
    /*public ngOnDestroy(): void {
      this._destroy$.next();
      this._destroy$.complete();
    }*/
    /*
      private _listenTransformStream() {
        combineLatest([this._phoneMetadataService.ready$, this._countryService.ready$])
          .pipe(
            filter(([ metadataState, countryState]) => metadataState && countryState),
            switchMap(() => this._transform$),
            map((value) => this._transform(value))
          )
          .subscribe((value) => {
            this._displayValue = value;
  
            this._cdRef.markForCheck();
          })
      }
  
      private _transform(value) {
        this._fallbackParse(value);
      }
  
      private _transform(value) {
        let phoneNumber: PhoneNumber;
        let result = '';
  
        try {
          // In case when string received we must parse it before continue
          // otherwise we should transform passed object into PhoneNumber instance
          switch (typeof value) {
            case 'string': {
              phoneNumber = this._phoneService.parsePhoneNumber(value);
            } break;
  
            case 'number': {
              phoneNumber = this._phoneService.parsePhoneNumber(`${value}`);
            } break;
  
            case 'object': {
              debugger;
              phoneNumber = this._phoneService.parsePhoneNumber(`${value.code}${value.number}`);
              phoneNumber.ext = value.ext;
            } break;
          }
  
          const phoneValueObject = this._phoneService.phoneNumberToPhoneValueObject(phoneNumber);
          const country = this._countryService.countryByCode(phoneNumber.country);
  
          result = `${country.emoji} ${phoneValueObject.code} ${phoneValueObject.number}`;
  
          if (phoneNumber.ext) {
            const extText = this._phoneService.getExtPrefix(phoneNumber.country);
  
            result += ` ${extText} ${phoneNumber.ext}`;
          }
  
        } catch (e) {
          result = this._fallbackParse(value);
        }
  
        return result;
      }
    */
    transform(value) {
        if (!value) {
            return '';
        }
        // if (this._value !== value) {
        //   this._value = value;
        //
        //   if (!this._ready) {
        //     this._displayValue = this._fallbackParse(value);
        //   }
        //
        //   this._transform$.next(value);
        // }
        return this._fallbackParse(value);
    }
    _fallbackParse(value) {
        let str;
        switch (typeof value) {
            case 'number':
                {
                    str = formatPhoneNumber('' + value);
                }
                break;
            case 'object':
                {
                    str = `${value.countryCode} ${value.number}`;
                    if (value.emoji) {
                        str = `${value.emoji} ${str}`;
                    }
                    if (value.ext) {
                        str = `${str} ext. ${value.ext}`;
                    }
                }
                break;
            default: {
                str = formatPhoneNumber(value);
            }
        }
        return str;
    }
}
FsPhonePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhonePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FsPhonePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhonePipe, name: "fsPhone" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhonePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsPhone',
                }]
        }] });

class FsPhoneModule {
    static forRoot(config = {}) {
        return {
            ngModule: FsPhoneModule,
            providers: [
                { provide: PHONE_CONFIG_ROOT, useValue: config },
                {
                    provide: PHONE_CONFIG,
                    useFactory: phoneConfigFactory,
                    deps: [PHONE_CONFIG_ROOT, [new Optional(), FS_PHONE_CONFIG]],
                },
            ],
        };
    }
}
FsPhoneModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsPhoneModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneModule, declarations: [FsPhoneComponent,
        FsPhoneFieldDeprecatedComponent,
        FsPhoneFieldComponent,
        FsPhoneDirective,
        FsPhonePipe], imports: [FormsModule,
        CommonModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        FsSkeletonModule,
        FsCountryModule,
        ReactiveFormsModule,
        PortalModule], exports: [FsPhoneComponent,
        FsPhoneFieldDeprecatedComponent,
        FsPhoneFieldComponent,
        FsPhoneDirective,
        FsPhonePipe] });
FsPhoneModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneModule, imports: [[
            FormsModule,
            CommonModule,
            MatSelectModule,
            MatInputModule,
            MatFormFieldModule,
            FsSkeletonModule,
            FsCountryModule,
            ReactiveFormsModule,
            PortalModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsPhoneModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FormsModule,
                        CommonModule,
                        MatSelectModule,
                        MatInputModule,
                        MatFormFieldModule,
                        FsSkeletonModule,
                        FsCountryModule,
                        ReactiveFormsModule,
                        PortalModule,
                    ],
                    exports: [
                        FsPhoneComponent,
                        FsPhoneFieldDeprecatedComponent,
                        FsPhoneFieldComponent,
                        FsPhoneDirective,
                        FsPhonePipe,
                    ],
                    declarations: [
                        FsPhoneComponent,
                        FsPhoneFieldDeprecatedComponent,
                        FsPhoneFieldComponent,
                        FsPhoneDirective,
                        FsPhonePipe,
                    ],
                }]
        }] });
function phoneConfigFactory(config, fsConfig) {
    return Object.assign(Object.assign({}, fsConfig), config);
}

function extractPhoneData(obj) {
    var _a, _b;
    const phone = {};
    if (!!obj.phone) {
        phone.number = (_a = obj.phone) === null || _a === void 0 ? void 0 : _a.toString();
    }
    if (obj.phoneCountryCode) {
        phone.countryCode = (_b = obj.phoneCountryCode) === null || _b === void 0 ? void 0 : _b.toString();
    }
    if (obj.phoneCountry) {
        phone.isoCode = obj.phoneCountry;
    }
    if (obj.phoneExt) {
        phone.ext = obj.phoneExt;
    }
    return phone;
}

// import { IFsPhoneData } from '../interfaces/entity-with-phone-data';
function setPhoneData(target, phone) {
    var _a;
    const result = Object.assign(Object.assign({}, target), { phone: phone === null || phone === void 0 ? void 0 : phone.number, phoneCountry: phone === null || phone === void 0 ? void 0 : phone.isoCode, phoneCountryCode: (_a = parseInt(phone.countryCode, 10)) !== null && _a !== void 0 ? _a : null });
    if (phone.ext) {
        result.phoneExt = phone.ext;
    }
    return result;
}

function stringsDiff(str1, str2) {
    if (typeof (str1) !== 'string' && typeof (str2) !== 'string') {
        return;
    }
    const strLen1 = str1.length;
    const strLen2 = str2.length;
    const maxLen = Math.max(strLen1, strLen2);
    for (let i = 0; i <= maxLen - 1; i++) {
        if (str1.charAt(i) !== str2.charAt(i)) {
            return str1.charAt(i);
        }
    }
    return;
}

/**
 * Generated bundle index. Do not edit.
 */

export { FS_PHONE_CONFIG, FsPhoneComponent, FsPhoneDirective, FsPhoneFieldComponent, FsPhoneFieldDeprecatedComponent, FsPhoneModule, FsPhonePipe, PhoneMetadataService, PhoneService, extractPhoneData, formatPhoneNumber, setPhoneData };
